import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Phase B+2-1 — 処方箋カード PDF 生成 utility (旧
 * `old/apps/user/src/components/ecw/IdeaCardList.tsx` L344-490 `handleGeneratePDF` を
 * pure async function に抽出)。
 *
 * 役割:
 *   - 与えられた template 要素 (`ReportPdfTemplate` がレンダした隠し DOM) を section 単位で
 *     html2canvas キャプチャし、jsPDF で A4 縦に積み上げて save する。
 *   - 地図 element があれば事前にキャプチャして template 内の地図 img に dataURL を流し込む。
 *   - template 内の `<img data-photo-path="...">` を `resolvePhotoUrl` callback で
 *     download URL に置換 (Firebase Storage path → HTTPS URL の取得は呼出側責務)。
 *
 * 旧 fallback path (`elementsToCapture.length === 0`):
 *   旧と同じく container 全体を 1 canvas にしてページ分割する fallback を残す。ただし
 *   v2 で `.pdf-section` を確実に貼ってあるので通常は通らない (defense-in-depth)。
 *
 * エラー方針:
 *   - 例外は throw、呼出側で toast / alert
 *   - 個別の img onerror は無視 (CORS / 失効 token で画像 1 枚抜けても PDF 生成自体は続行)
 */

export interface GenerateIdeaPdfOptions {
  /** ReportPdfTemplate がレンダした隠し DOM の root 要素。 */
  templateRef: HTMLDivElement;
  /** 保存ファイル名 (拡張子込み)。 */
  fileName: string;
  /** 詳細 modal 内の MapLibre 地図 element (任意、なければ地図 section は "No Map" のまま)。 */
  mapElement?: HTMLDivElement | null;
  /**
   * `<img data-photo-path="...">` を download URL に置換する callback (任意)。
   * `null` を返したら img は src 未設定のまま (No Photo placeholder 風)。
   */
  resolvePhotoUrl?: (path: string) => Promise<string | null>;
}

/** 与えられた template element を A4 縦 PDF として保存する。 */
export async function generateIdeaPdf(opts: GenerateIdeaPdfOptions): Promise<void> {
  const { templateRef, fileName, mapElement, resolvePhotoUrl } = opts;

  // 1. 地図がある場合は事前にキャプチャして、template の "地図" img の src に注入する。
  if (mapElement) {
    try {
      const mCanvas = await html2canvas(mapElement, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        logging: false,
      });
      const mapDataUrl = mCanvas.toDataURL('image/png');
      const mapImg = templateRef.querySelector<HTMLImageElement>('img[alt="地図"]');
      if (mapImg) {
        mapImg.src = mapDataUrl;
      }
    } catch (e) {
      // 地図キャプチャ失敗は致命傷ではない (旧仕様も console.error して続行)
      console.error('[generateIdeaPdf] map capture failed', e);
    }
  }

  // 2. data-photo-path 属性付き <img> を download URL に置換 (Firebase Storage path 解決)。
  if (resolvePhotoUrl) {
    const photoImgs = Array.from(
      templateRef.querySelectorAll<HTMLImageElement>('img[data-photo-path]'),
    );
    await Promise.all(
      photoImgs.map(async (img) => {
        const path = img.getAttribute('data-photo-path');
        if (!path) return;
        try {
          const url = await resolvePhotoUrl(path);
          if (url) img.src = url;
        } catch (e) {
          console.warn('[generateIdeaPdf] photo url resolve failed', path, e);
        }
      }),
    );
  }

  // 3. 全 img の load 完了を待つ (gray box / 抜け防止、旧 L367-374 踏襲)。
  //    jsdom テスト環境では onload が発火しないので 3000ms timeout で打ち切る
  //    (本番 browser では img.complete=true もしくは onload で即 resolve)。
  const images = Array.from(templateRef.querySelectorAll('img'));
  await Promise.all(
    images.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const done = () => resolve();
        img.onload = done;
        img.onerror = done; // 1 枚抜けても続行
        setTimeout(done, 3000); // safety timeout (CORS hang / jsdom 等)
      });
    }),
  );
  // ブラウザ paint 待ち (旧 L377 踏襲、300ms)
  await new Promise((r) => setTimeout(r, 300));

  // 4. .pdf-section ごとに canvas 化して jsPDF に積む。
  const elementsToCapture = Array.from(templateRef.querySelectorAll<HTMLElement>('.pdf-section'));

  // 4a. fallback: section が 1 つも無い場合は container 全体を 1 canvas にして分割。
  if (elementsToCapture.length === 0) {
    const canvas = await html2canvas(templateRef, {
      useCORS: true,
      allowTaint: true,
      scale: 2,
      logging: false,
      width: 794,
      windowWidth: 794,
    });
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentHeightOnPage = pdfHeight - margin * 2;
    const imgWidth = pdfWidth;
    const totalImgHeight = (canvas.height * pdfWidth) / canvas.width;
    let heightLeft = totalImgHeight;
    let position = 0;
    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', 0, margin, imgWidth, totalImgHeight);
    heightLeft -= contentHeightOnPage;
    while (heightLeft > 0) {
      position = heightLeft - totalImgHeight + margin;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, totalImgHeight);
      heightLeft -= contentHeightOnPage;
    }
    pdf.save(fileName);
    return;
  }

  // 4b. 通常 path: section ごとに canvas 化 → page break 制御。
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentHeightOnPage = pdfHeight - margin * 2;

  let currentY = margin;
  let isFirstPage = true;

  for (const el of elementsToCapture) {
    const canvas = await html2canvas(el, {
      useCORS: true,
      allowTaint: true,
      scale: 2,
      logging: false,
      width: 794,
      windowWidth: 794,
    });
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    if (currentY + imgHeight > pdfHeight - margin) {
      // section が現在ページに乗らない
      if (imgHeight > contentHeightOnPage) {
        // 1 page 超のジャンボ section: 分割して複数 page にまたがらせる (旧 L449-469 踏襲)
        if (!isFirstPage || currentY > margin) {
          pdf.addPage();
          currentY = margin;
        }
        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, 'PNG', 0, currentY, imgWidth, imgHeight);
        heightLeft -= pdfHeight - currentY - margin;
        currentY += imgHeight;
        while (heightLeft > 0) {
          position = heightLeft - imgHeight + margin;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= contentHeightOnPage;
          currentY = imgHeight - (imgHeight - heightLeft);
        }
        currentY = margin + heightLeft;
      } else {
        // 単 page に乗る size → 新規 page の先頭に貼る
        if (!isFirstPage || currentY > margin) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, margin, imgWidth, imgHeight);
        currentY = margin + imgHeight;
      }
    } else {
      // 現在 page にそのまま乗る
      pdf.addImage(imgData, 'PNG', 0, currentY, imgWidth, imgHeight);
      currentY += imgHeight;
    }

    isFirstPage = false;
  }

  pdf.save(fileName);
}
